import React, { useMemo } from 'react';
import { useTranslation } from 'localization';
import './PasswordStrengthIndicatorMolecule.scss';

export interface PasswordStrengthIndicatorMoleculeProps {
  password: string;
  className?: string;
}

interface StrengthCriteria {
  hasLength: boolean;
  hasUppercase: boolean;
  hasLowercase: boolean;
  hasNumber: boolean;
  hasSymbol: boolean;
}

export type StrengthLevel = 'weak' | 'medium' | 'strong';

const PasswordStrengthIndicatorMolecule: React.FC<
  PasswordStrengthIndicatorMoleculeProps
> = ({ password, className = '' }) => {
  const { t } = useTranslation();

  const criteria = useMemo((): StrengthCriteria => {
    return {
      hasLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSymbol: /[^A-Za-z0-9]/.test(password),
    };
  }, [password]);

  const strength = useMemo((): StrengthLevel => {
    const metCriteria = Object.values(criteria).filter(Boolean).length;

    if (metCriteria <= 2) return 'weak';
    if (metCriteria <= 4) return 'medium';
    return 'strong';
  }, [criteria]);

  const strengthPercentage = useMemo((): number => {
    const metCriteria = Object.values(criteria).filter(Boolean).length;
    return (metCriteria / 5) * 100;
  }, [criteria]);

  if (!password) {
    return null;
  }

  return (
    <div
      className={`password-strength-indicator-molecule ${className}`.trim()}
    >
      <div className="password-strength-indicator-molecule__bar">
        <div
          className={`password-strength-indicator-molecule__fill password-strength-indicator-molecule__fill--${strength}`}
          style={{ width: `${strengthPercentage}%` }}
        />
      </div>

      <div className="password-strength-indicator-molecule__label">
        <span
          className={`password-strength-indicator-molecule__level password-strength-indicator-molecule__level--${strength}`}
        >
          {t(`auth.passwordStrength.${strength}`)}
        </span>
      </div>

      <ul className="password-strength-indicator-molecule__criteria">
        <li
          className={
            criteria.hasLength
              ? 'password-strength-indicator-molecule__criterion--met'
              : 'password-strength-indicator-molecule__criterion--unmet'
          }
        >
          <i
            className={`pi ${criteria.hasLength ? 'pi-check' : 'pi-times'}`}
          />
          {t('auth.passwordStrength.criteria.length')}
        </li>
        <li
          className={
            criteria.hasUppercase
              ? 'password-strength-indicator-molecule__criterion--met'
              : 'password-strength-indicator-molecule__criterion--unmet'
          }
        >
          <i
            className={`pi ${criteria.hasUppercase ? 'pi-check' : 'pi-times'}`}
          />
          {t('auth.passwordStrength.criteria.uppercase')}
        </li>
        <li
          className={
            criteria.hasLowercase
              ? 'password-strength-indicator-molecule__criterion--met'
              : 'password-strength-indicator-molecule__criterion--unmet'
          }
        >
          <i
            className={`pi ${criteria.hasLowercase ? 'pi-check' : 'pi-times'}`}
          />
          {t('auth.passwordStrength.criteria.lowercase')}
        </li>
        <li
          className={
            criteria.hasNumber
              ? 'password-strength-indicator-molecule__criterion--met'
              : 'password-strength-indicator-molecule__criterion--unmet'
          }
        >
          <i
            className={`pi ${criteria.hasNumber ? 'pi-check' : 'pi-times'}`}
          />
          {t('auth.passwordStrength.criteria.number')}
        </li>
        <li
          className={
            criteria.hasSymbol
              ? 'password-strength-indicator-molecule__criterion--met'
              : 'password-strength-indicator-molecule__criterion--unmet'
          }
        >
          <i
            className={`pi ${criteria.hasSymbol ? 'pi-check' : 'pi-times'}`}
          />
          {t('auth.passwordStrength.criteria.symbol')}
        </li>
      </ul>
    </div>
  );
};

export default PasswordStrengthIndicatorMolecule;
